import React from 'react';
import { RuleSession, RuleSettings } from "../types";
import NumberField from "./NumberField";
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function Button(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return <button className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-gray-200 ${className}`} {...rest} />
}

export default function RuleSettingsForm({ value, onChange }: { value: RuleSettings; onChange: (v: RuleSettings) => void }) {
  const v = value;
  const { t } = useTranslation();
  const set = (patch: Partial<RuleSettings>) => onChange({ ...v, ...patch });
  const updateSession = (idx: number, patch: Partial<RuleSession>) => {
    const arr = v.allowedSessions.slice();
    arr[idx] = { ...arr[idx], ...patch };
    set({ allowedSessions: arr });
  };
  const addSession = () => set({ allowedSessions: [...v.allowedSessions, { start: "", end: "", label: "", isActive: false }] });
  const removeSession = (idx: number) => set({ allowedSessions: v.allowedSessions.filter((_, i) => i !== idx) });

  return (
    <div className="grid grid-cols-1 gap-3 text-sm">
      <div className="grid md:grid-cols-3 gap-3">
        <NumberField
          label={t("MaxRiskPercent")}
          value={v.maxRiskPercent}
          step={0.1}
          onChange={(n) => set({ maxRiskPercent: n })}
        />
        <NumberField
          label={t("MaxPositions")}
          value={v.maxPositions}
          step={1}
          onChange={(n) => set({ maxPositions: Math.max(0, Math.round(n)) })}
        />
        <NumberField
          label={t("MaxLotsPerTrade")}
          value={v.maxLotsPerTrade}
          step={0.01}
          onChange={(n) => set({ maxLotsPerTrade: Math.max(0, n) })}
        />
        <NumberField
          label={t('MaxSLPercent')}
          value={v.maxSLPercent}
          step={0.1}
          onChange={(n) => set({ maxSLPercent: n })}
        />
        <NumberField
          label={t('MaxDailyDDPercent')}
          value={v.maxDailyDDPercent}
          step={0.1}
          onChange={(n) => set({ maxDailyDDPercent: n })}
        />
        <NumberField
          label={t('MinRRAllowed')}
          value={v.minRRAllowed}
          step={0.1}
          onChange={(n) => set({ minRRAllowed: n })}
        />
        <NumberField
          label={t('MaxSLTPChangePercent')}
          value={v.maxSLTPChangePercent}
          step={1}
          onChange={(n) => set({ maxSLTPChangePercent: n })}
        />
      </div>
      <div className="flex flex-col md:flex-row flex-wrap gap-4">
        <label className="!flex items-center gap-2 mt-2 md:w-[40%]">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={v.requireFirstTradeGoal}
            onChange={(e) => set({ requireFirstTradeGoal: e.target.checked })}
          />
          <span>{t('RequireRRtargetdeclaration')}</span>
        </label>
        <label className="!flex items-center gap-2 mt-2 md:w-[40%]">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={v.requireJournalBeforeNewTrade}
            onChange={(e) =>
              set({ requireJournalBeforeNewTrade: e.target.checked })
            }
          />
          <span>{t('Requirewritingajournal')}</span>
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-gray-700">{t('Allowedsessions')}</span>
          {/* <button
            onClick={addSession}
            className="flex items-center gap-1 px-3  py-2  text-white !rounded-lg bg-[#E9B460] hover:bg-[#d1a154] transition-colors duration-200 text-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              className="lucide lucide-plus w-4 h-4"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            <span className="inline">{t('Addsession')}</span>
          </button> */}
        </div>
        <div className="space-y-2">
          {v.allowedSessions.map((s, idx) => (
            <div key={idx} className="border border-gray-300 rounded-md p-2 flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={s.isActive !== false}
                  onChange={(e) => updateSession(idx, { isActive: e.target.checked })}
                />
                <span className="text-gray-700 font-medium truncate flex-1 min-w-0">{s.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-md border border-gray-300 px-2 py-1 bg-gray-100 text-center w-16">{s.start}</span>
                <span className="rounded-md border border-gray-300 px-2 py-1 bg-gray-100">–</span>
                <span className="rounded-md border border-gray-300 px-2 py-1 bg-gray-100 text-center w-16">{s.end}</span>
                <span className="text-gray-700">UTC</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-xs text-gray-500">
        {t('ChangeNote')}
      </div>
    </div>
  );
}
